import itemQueries from '@apis/queries/itemQueries';
import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import useGlobalName from '@hooks/useGlobalName';
import { OrderLine } from '@models/order';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface Props {
    line: OrderLine;
}

/** 주문 상품 한 줄 (이미지·상품명·옵션·수량·금액) */
function OrderItemRow({ line }: Props) {
    const {
        itemName, quantity, personalOptions, defaultOptions, itemImageUrl, totalAmount, shopItemId,
    } = line;
    const { t } = useTranslation('order');
    const priceUnit = usePriceUnit();
    const { data: itemDetail } = useQuery(itemQueries.detail(shopItemId));
    const globalItemName = useGlobalName({
        name: itemName,
        nameJa: itemDetail?.nameJa,
        nameEn: itemDetail?.nameEn,
    });
    const options = [...defaultOptions, ...personalOptions]
        .map((group) => group.options)
        .flat();
    return (
        <Row fullWidth spacing={4} align='center'>
            <Thumbnail src={itemImageUrl}
                width={100}
                height={100}
                alt={t('items.imageAlt')} />
            <Column spacing={2} fullWidth css={{ minWidth: 0 }}>
                <BreakText size='title' fontWeight='medium' color={colors.textPrimary}>
                    {globalItemName}
                </BreakText>
                {options.length !== 0 && (
                    <Column spacing={1} fullWidth>
                        {options.map((option) => (
                            <BreakText key={option.shopOptionId}
                                size='body-long'
                                color={colors.textSecondary}>
                                {option.quantity > 1
                                    ? `${option.optionName} x${option.quantity}`
                                    : option.optionName}
                            </BreakText>
                        ))}
                    </Column>
                )}
                <Row fullWidth justify='space-between'>
                    <Text fontWeight='medium' color={colors.textSecondary}>
                        {t('items.quantity', { count: quantity })}
                    </Text>
                    <Text fontWeight='medium' color={colors.textPrimary}>
                        {totalAmount.toLocaleString()}{priceUnit}
                    </Text>
                </Row>
            </Column>
        </Row>
    );
}

const Thumbnail = styled(Image)`
    border-radius: var(--radius-xs);
    flex-shrink: 0;
    height: 100px;
    object-fit: cover;
    width: 100px;
`;

const BreakText = styled(Text)`
    word-break: break-word;
`;

export default OrderItemRow;
